/// <amd-module name="Wit/WorkItemForm/Common/Services/WorkItemFormCommentService" />
import { CommentReactionType } from "Comments/Common/Contracts";
import { createReactionsMap, reactionsToReactionMap } from "Comments/Reactions/Util";
import { DisplayNameStorageKeyTranslationService, translateStorageKeysToDisplayNames } from "Mention/Common/DisplayNameStorageKeyTranslationService";
import { IReadyableObservableArray, ReadyableObservableArray } from "VSS/Core/Observable";
import { PersonaDetailsProvider } from "VSS/Features/Identities/PersonaDetailsProvider";
import { IVssPageContext, Services, VssService } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import * as WorkItemTracking from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { CommentFormat } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { IWorkItemTrackingRestClient, getWorkItemTrackingClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { IWorkItem, WorkItemDiscussionFormat } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { IWorkItemEventService, IWorkItemEventType } from "Wit/WorkItemModel/Services/IWorkItemEventService";
import { htmlSanitizerPreserveClassAndMentionOptions } from "Wit/WorkItemModel/Utils";
import { filterXSS } from "xss";
import { ICommentWithReactions, IWorkItemFormCommentService } from "./IWorkItemFormCommentService";
import { IWorkItemFormService } from "./IWorkItemFormService";

// TODO: revisit error handling and externalize error messages

interface InternalWorkItemDataClass {
    totalComments?: number;
    continuationToken?: string;
    lastRevisionSeen?: number;
}

class WorkItemFormCommentService extends VssService implements IWorkItemFormCommentService {
    private witRestClient: IWorkItemTrackingRestClient;
    private workItemFormService: IWorkItemFormService;
    private personaDetailsProvider: PersonaDetailsProvider;
    private displayNameTranslationService: DisplayNameStorageKeyTranslationService;
    private workItemEventService: IWorkItemEventService;

    // Comments
    private internalWorkItemData: { [id: number]: InternalWorkItemDataClass } = {};
    private observableComments: { [id: number]: ReadyableObservableArray<ICommentWithReactions> } = {};
    private commentPromise: Promise<unknown> | undefined;
    private alwaysEmpty = new ReadyableObservableArray<ICommentWithReactions>([], true);

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        this.witRestClient = getWorkItemTrackingClient(pageContext);
        this.workItemFormService = pageContext.getService<IWorkItemFormService>("IWorkItemFormService");
        this.workItemEventService = pageContext.getService<IWorkItemEventService>("IWorkItemEventService");
        this.displayNameTranslationService = pageContext.getService<DisplayNameStorageKeyTranslationService>(
            "display-name-storage-key-translation-service"
        );
        this.personaDetailsProvider = new PersonaDetailsProvider(pageContext);
    }

    public getObservable(workItem: IWorkItem): IReadyableObservableArray<ICommentWithReactions> {
        if (workItem.isNew()) {
            return this.alwaysEmpty;
        }

        // Setup internal data if not available yet
        if (!this.internalWorkItemData[workItem.id]) {
            this.internalWorkItemData[workItem.id] = {};
            this.internalWorkItemData[workItem.id].lastRevisionSeen = workItem.revision?.value;
        }

        if (!this.observableComments[workItem.id]) {
            this.observableComments[workItem.id] = new ReadyableObservableArray<ICommentWithReactions>([], false);
        }

        return this.observableComments[workItem.id];
    }

    public resetCommentsData(workItem: IWorkItem): void {
        if (workItem.isNew()) {
            return;
        }

        this.internalWorkItemData[workItem.id] = { lastRevisionSeen: workItem.revision?.value };
    }

    public async getComments(workItem: IWorkItem, top: number): Promise<unknown> {
        if (workItem.isNew()) {
            return;
        }

        const commentObservable = this.getObservable(workItem);
        let revisionOutdated = false;
        if (
            this.internalWorkItemData[workItem.id] &&
            this.internalWorkItemData[workItem.id].lastRevisionSeen &&
            this.internalWorkItemData[workItem.id].lastRevisionSeen != workItem.revision.value
        ) {
            revisionOutdated = true;
            this.resetCommentsData(workItem);
        }
        const totalCommentsInWorkItem: number | undefined = this.internalWorkItemData[workItem.id].totalComments;

        if (this.commentPromise) {
            // We are already in a comments REST API request, return that
            return this.commentPromise;
        } else if (
            commentObservable.value === undefined ||
            revisionOutdated ||
            (commentObservable.value &&
                top > commentObservable.length &&
                (totalCommentsInWorkItem === undefined || commentObservable.length < totalCommentsInWorkItem))
        ) {
            // We are not currently requesting and we need to get more from server
            const numCommentsReceived: number = commentObservable.length ?? 0;
            const promise = this.witRestClient.getComments(
                workItem.project.guid,
                workItem.id,
                top - (revisionOutdated ? 0 : numCommentsReceived),
                this.internalWorkItemData[workItem.id].continuationToken,
                false,
                WorkItemTracking.CommentExpandOptions.All,
                WorkItemTracking.CommentSortOrder.Desc
            );
            // set promise before awaiting
            this.commentPromise = promise;
            commentObservable.ready.value = false;

            const commentList = await promise;

            let commentsWithReactions:
                | {
                      comment: WorkItemTracking.Comment;
                      reactionPromises: {};
                      reactions: ReturnType<typeof createReactionsMap>;
                  }[]
                | null = null;
            if (isFeatureFlagEnabled(this.pageContext, FeatureFlags.IsMarkdownDiscussionEnabled, false)) {
                commentsWithReactions = [];
                for (let comment of commentList.comments) {
                    if (comment.mentions) {
                        for (let mention of comment.mentions) {
                            if (this.displayNameTranslationService.isStorageKeyAlreadyAdded(mention.targetId)) {
                                continue;
                            }
                            const identity = await this.personaDetailsProvider.getEntityFromUniqueAttribute(mention.targetId);
                            if (!identity) {
                                continue;
                            }
                            const displayText = this.displayNameTranslationService.getIdentityId(identity);
                            this.displayNameTranslationService.addDisplayNameAndStorageKey(displayText, mention.targetId);
                        }
                    }

                    comment.text = await translateStorageKeysToDisplayNames(this.pageContext, comment.text);

                    commentsWithReactions.push({ comment, reactionPromises: {}, reactions: createReactionsMap(comment.reactions) });
                }
            } else {
                commentsWithReactions = commentList.comments.map(comment => ({
                    comment,
                    reactionPromises: {},
                    reactions: createReactionsMap(comment.reactions)
                }));
            }

            if (!commentObservable.value || revisionOutdated) {
                commentObservable.value = commentsWithReactions;
            } else {
                commentObservable.push(...commentsWithReactions);
            }
            this.internalWorkItemData[workItem.id].continuationToken = commentList.continuationToken;
            this.internalWorkItemData[workItem.id].totalComments = commentList.totalCount;
            this.commentPromise = undefined;
            commentObservable.ready.value = true;

            this.workItemEventService.fireEvent({ workItem: workItem }, IWorkItemEventType.WorkItemCommentsUpdated);
            return;
        }
    }

    public async syncCommentReactions(workItem: IWorkItem, commentId: number) {
        const commentObservable = this.getObservable(workItem);
        const commentIndex = commentObservable.value.findIndex(comment => comment.comment.id === commentId);
        if (commentIndex === -1) {
            return;
        }

        const reactions = await this.witRestClient.getCommentReactions(workItem.project.guid, workItem.id, commentId);

        commentObservable.change(commentIndex, { ...commentObservable.value[commentIndex], reactions: reactionsToReactionMap(reactions) });
    }

    public getTotalCommentsInWorkItem(workItem: IWorkItem): number | undefined {
        if (workItem.isNew()) {
            return 0;
        }

        return this.internalWorkItemData[workItem.id].totalComments;
    }

    public async addComment(workItem: IWorkItem, comment: string): Promise<WorkItemTracking.Comment> {
        let format = WorkItemTracking.CommentFormat.Html;
        if (isFeatureFlagEnabled(this.pageContext, FeatureFlags.IsMarkdownDiscussionEnabled, false)) {
            const settingsService = this.pageContext.getService<ISettingsService>("ISettingsService");
            const formatSettings = settingsService.getEntry<string>("WorkItemDiscussionSettings/CommentFormatState", SettingsUserScope.Me);
            if (formatSettings === WorkItemDiscussionFormat.Markdown) {
                format = WorkItemTracking.CommentFormat.Markdown;
            }
        }
        const create: WorkItemTracking.CommentCreate = {
            text: format == WorkItemTracking.CommentFormat.Markdown ? filterXSS(comment, htmlSanitizerPreserveClassAndMentionOptions) : comment
        };
        this.workItemFormService.resetHistoryData();

        const addCommentPromise = this.witRestClient.addWorkItemComment(create, workItem.project.guid, workItem.id, format);
        return addCommentPromise;
    }

    public async deleteComment(workItem: IWorkItem, commentId: number): Promise<void> {
        await this.witRestClient.deleteComment(workItem.project.guid, workItem.id, commentId);
        this.workItemFormService.resetHistoryData();
    }

    public async updateComment(workItem: IWorkItem, commentId: number, newValue: string, format?: CommentFormat): Promise<WorkItemTracking.Comment> {
        const update: WorkItemTracking.CommentUpdate = {
            text: format == WorkItemTracking.CommentFormat.Markdown ? filterXSS(newValue, htmlSanitizerPreserveClassAndMentionOptions) : newValue
        };

        const promise = await this.witRestClient.updateWorkItemComment(
            update,
            workItem.project.guid,
            workItem.id,
            commentId,
            format ?? CommentFormat.Html
        );
        this.workItemFormService.resetHistoryData();
        return promise;
    }

    public async getRecentlyReactedUsers(
        workItem: IWorkItem,
        commentId: number,
        reactionType: WorkItemTracking.CommentReactionType,
        top: number
    ): Promise<void> {
        const commentObservable = this.observableComments[workItem.id];
        const commentWithReactions = commentObservable?.value?.find(c => c.comment.id === commentId);
        const info = commentWithReactions?.reactions[reactionType];
        if (info && info.engagedUsers.length === 0 && !commentWithReactions?.reactionPromises[reactionType]) {
            const promise = this.witRestClient.getEngagedUsers(workItem.project.guid, workItem.id, commentId, reactionType, top);
            commentWithReactions!.reactionPromises[reactionType] = promise;
            const users = await promise;
            info.engagedUsers = users.map(identityRef => ({ identityId: identityRef.id, identityDisplayName: identityRef.displayName }));
            // notify of change
            commentObservable.value = commentObservable.value;
        }
    }

    /**
     * Reset the reaction cache for the given comment and reaction type
     */
    private _resetEngagedUsers(workItem: IWorkItem, commentId: number, reactionType: WorkItemTracking.CommentReactionType) {
        const commentObservable = this.observableComments[workItem.id];
        const commentWithReactions = commentObservable?.value?.find(c => c.comment.id === commentId);
        if (commentWithReactions) {
            commentWithReactions.reactionPromises[reactionType] = undefined;
            const baseReactions = createReactionsMap(commentWithReactions.comment.reactions);
            commentWithReactions.reactions[reactionType] = baseReactions[reactionType];
        }
    }

    public async createCommentReaction(workItem: IWorkItem, commentId: number, reactionType: CommentReactionType): Promise<void> {
        const thisReactionType = reactionType as unknown as WorkItemTracking.CommentReactionType;
        const commentObservable = this.getObservable(workItem);
        await this.witRestClient.createCommentReaction(workItem.project.guid, workItem.id, commentId, thisReactionType);

        const comment = commentObservable.value?.find(c => c.comment.id == commentId)?.comment;
        if (comment) {
            if (!comment.reactions) {
                comment.reactions = [
                    {
                        commentId: comment.id,
                        count: 1,
                        isCurrentUserEngaged: true,
                        type: thisReactionType,
                        _links: undefined,
                        url: ""
                    }
                ];
            } else {
                const react: WorkItemTracking.CommentReaction | undefined = comment.reactions.find(r => r.type === thisReactionType);
                if (!react) {
                    if (!comment.reactions) {
                        comment.reactions = [];
                    }
                    comment.reactions.push({
                        commentId: comment.id,
                        count: 1,
                        isCurrentUserEngaged: true,
                        type: thisReactionType,
                        _links: undefined,
                        url: ""
                    });
                } else {
                    react.count++;
                    react.isCurrentUserEngaged = true;
                }
            }
        }
        this._resetEngagedUsers(workItem, commentId, thisReactionType);
    }

    public async deleteCommentReaction(workItem: IWorkItem, commentId: number, reactionType: CommentReactionType): Promise<void> {
        const thisReactionType = reactionType as unknown as WorkItemTracking.CommentReactionType;
        const commentObservable = this.getObservable(workItem);
        await this.witRestClient.deleteCommentReaction(workItem.project.guid, workItem.id, commentId, thisReactionType);

        const comment = commentObservable.value?.find(c => c.comment.id == commentId)?.comment;
        if (comment) {
            const react: WorkItemTracking.CommentReaction | undefined = comment.reactions.find(r => r.type === thisReactionType);
            if (react) {
                react.count--;
                react.isCurrentUserEngaged = false;
            }
        }
        this._resetEngagedUsers(workItem, commentId, thisReactionType);
    }
}

const serviceName = "IWorkItemFormCommentService";
Services.add(serviceName, { serviceFactory: WorkItemFormCommentService });
